/**
 * Reduces NDJSON stream events from /v2/messages and /v2/cards/resolve into
 * the local Turn[] view-model.
 *
 * Spec: v2-design/CHAT_DAG_PROGRESS_v0_spec.md §5-6.
 *
 * row_inserted is the single source of truth for user-text, assistant-text,
 * cards, dag_progress, calc_progress, and scope_change. row_updated mutates
 * a dag_progress row's stages in place OR a calc_progress row's rows /
 * title in place. final/error are control events — final carries
 * cancelled / completed kinds that don't have a corresponding row.
 */
import {
  CalcProgressRow,
  ConversationHistoryRow,
  DagStage,
  HandlerResponse,
  StreamEvent,
  Turn,
  CardType,
} from './types';

const CARD_TYPE_SUFFIX = '_card';

function isCardType(t: string): t is CardType {
  return t.endsWith(CARD_TYPE_SUFFIX);
}

function tsFromIso(iso: string): number {
  const n = Date.parse(iso);
  return Number.isFinite(n) ? n : Date.now();
}

function turnFromRow(row: ConversationHistoryRow): Turn | null {
  const ts = tsFromIso(row.inserted_at);
  if (row.type === 'text') {
    const text = String(row.content?.text ?? '');
    if (row.role === 'user') {
      return { kind: 'user', text, ts, rowId: row.id };
    }
    return { kind: 'assistant_text', text, ts, rowId: row.id };
  }
  if (row.type === 'dag_progress') {
    const stages = (row.content?.stages as DagStage[] | undefined) ?? [];
    const subAgentType = String(row.content?.sub_agent_type ?? '');
    return {
      kind: 'dag_progress',
      rowId: row.id,
      subAgentType,
      stages,
      planId: row.plan_id,
      ts,
    };
  }
  if (row.type === 'calc_progress') {
    const title = String(row.content?.title ?? '');
    const rows = (row.content?.rows as CalcProgressRow[] | undefined) ?? [];
    return {
      kind: 'calc_progress',
      rowId: row.id,
      title,
      rows,
      planId: row.plan_id,
      ts,
    };
  }
  if (row.type === 'scope_change') {
    return { kind: 'system', level: 'info', text: 'Scope updated.', ts };
  }
  if (isCardType(row.type)) {
    return {
      kind: 'card',
      cardType: row.type,
      content: row.content,
      planId: row.plan_id,
      ts,
      rowId: row.id,
    };
  }
  // Unknown row types are ignored at the reducer; surface in dev console
  // if you're debugging a new substrate type that hasn't been wired here.
  return null;
}

function findTurnIndexByRowId(turns: Turn[], rowId: string): number {
  for (let i = turns.length - 1; i >= 0; i -= 1) {
    const t = turns[i];
    if ('rowId' in t && t.rowId === rowId) return i;
  }
  return -1;
}

/**
 * Apply one stream event to the turns array. Pure: returns a new array on
 * mutation, or the same array reference when the event has no visible
 * effect (so React skips re-render).
 *
 * The caller is responsible for HandlerResponse handling via
 * applyFinalResponse — final events that carry control-only kinds
 * (cancelled / completed without a row) need a synthetic turn appended.
 */
export function applyStreamEvent(turns: Turn[], event: StreamEvent): Turn[] {
  if (event.kind === 'row_inserted') {
    // Idempotency: a row we've already seen (by id) is skipped. Defensive —
    // v0 streams don't replay, but a future late-join path might.
    if (findTurnIndexByRowId(turns, event.row.id) !== -1) return turns;
    const next = turnFromRow(event.row);
    if (!next) return turns;
    return [...turns, next];
  }
  if (event.kind === 'row_updated') {
    const idx = findTurnIndexByRowId(turns, event.row_id);
    if (idx === -1) return turns;
    const current = turns[idx];
    if (current.kind === 'dag_progress') {
      const stages =
        (event.content?.stages as DagStage[] | undefined) ?? current.stages;
      const updated: Turn = { ...current, stages };
      const next = turns.slice();
      next[idx] = updated;
      return next;
    }
    if (current.kind === 'calc_progress') {
      const title =
        (event.content?.title as string | undefined) ?? current.title;
      const rows =
        (event.content?.rows as CalcProgressRow[] | undefined) ?? current.rows;
      const updated: Turn = { ...current, title, rows };
      const next = turns.slice();
      next[idx] = updated;
      return next;
    }
    return turns;
  }
  // final / error: no turns mutation here. final-kind handling lives in
  // applyFinalResponse so the streaming sender can dispatch it once after
  // the loop completes (matches the existing ingestResponse contract).
  return turns;
}

/**
 * The handler's final response carries one of {text, card, scope_change,
 * cancelled, completed}. text / card / scope_change already arrived as
 * row_inserted events — there is no extra turn to append. cancelled and
 * completed-without-row are control states; render a system info turn for
 * cancelled. completed is currently a synonym for card (the same row with
 * status=completed) in v2 — no extra turn unless a future kind without a
 * row needs surfacing.
 */
export function applyFinalResponse(
  turns: Turn[],
  response: HandlerResponse
): Turn[] {
  if (response.kind === 'cancelled') {
    return [
      ...turns,
      {
        kind: 'system',
        level: 'info',
        text: `Plan ${response.plan_id ?? ''} cancelled.`,
        ts: Date.now(),
      },
    ];
  }
  return turns;
}
